/**
 * Generic Transfer Inventory Stepper
 * Matches AddInventoryStepper UI with numbered steps.
 * For products: groups by product+variant, supports pallet/case/unit breakdown.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog, Box, Typography, IconButton, Stepper, Step, StepLabel, StepContent,
  Button, CircularProgress, useTheme, alpha, TextField, Alert
} from '@mui/material';
import {
  Close as CloseIcon, SwapHoriz as SwapHorizIcon, ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { getTransferConfig } from '../transferConfigs';
import { useInventoryItems, useLocations } from '../../shared/useInventoryData';
import TransferItemsTable from './TransferItemsTable';
import LocationSelectDialog from './LocationSelectDialog';

export default function TransferInventoryStepper({
  open,
  onClose,
  onBack,
  inventoryType,
  companyId,
  onSuccess
}) {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [fromLocation, setFromLocation] = useState(null);
  const [toLocation, setToLocation] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [availableLots, setAvailableLots] = useState({});
  const [notes, setNotes] = useState('');

  // Search states (for location dialogs)
  const [fromSearchTerm, setFromSearchTerm] = useState('');
  const [toSearchTerm, setToSearchTerm] = useState('');
  const [fromLocationDialogOpen, setFromLocationDialogOpen] = useState(false);
  const [toLocationDialogOpen, setToLocationDialogOpen] = useState(false);
  const [clearedOtherStep, setClearedOtherStep] = useState(null);

  // Load data
  const { items } = useInventoryItems(inventoryType, companyId);
  const { locations } = useLocations(companyId);

  const isProduct = inventoryType === 'products';

  // Get config
  const config = React.useMemo(() => {
    try {
      return getTransferConfig(inventoryType);
    } catch (err) {
      console.error('Transfer config error:', err);
      return null;
    }
  }, [inventoryType]);

  // Sort locations: primary first, then by name
  const sortedLocations = React.useMemo(() => {
    if (!locations) return [];
    return [...locations].sort((a, b) => {
      const aPrimary = !!(a.is_main || a.is_primary);
      const bPrimary = !!(b.is_main || b.is_primary);
      if (aPrimary && !bPrimary) return -1;
      if (!aPrimary && bPrimary) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [locations]);

  const steps = [
    'Select From Location',
    'Select To Location',
    'Configure Transfer Items'
  ];

  // Reset on open/close
  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setFromLocation(null);
      setToLocation(null);
      setSelectedItems({});
      setAvailableLots({});
      setNotes('');
      setError(null);
      setFromSearchTerm('');
      setToSearchTerm('');
      setFromLocationDialogOpen(false);
      setToLocationDialogOpen(false);
      setClearedOtherStep(null);
    }
  }, [open]);

  useEffect(() => {
    setClearedOtherStep(null);
  }, [activeStep]);

  // Load available lots when transitioning to step 2
  useEffect(() => {
    if (fromLocation && items && items.length > 0 && activeStep === 2) {
      loadAvailableLots();
    }
  }, [fromLocation, items, activeStep]);

  const loadAvailableLots = async () => {
    if (!fromLocation) return;

    setLoading(true);
    try {
      const lotsData = {};
      for (const item of items) {
        const lots = await config.getInventoryItems(companyId, item.id, fromLocation.id);
        if (lots && lots.length > 0) {
          if (isProduct) {
            // Group by product+variant so each variant is its own section
            for (const lot of lots) {
              const variantKey = lot.variant_id || 'null';
              const key = `${item.id}_${variantKey}`;
              if (!lotsData[key]) lotsData[key] = [];
              lotsData[key].push(lot);
            }
          } else {
            lotsData[item.id] = lots;
          }
        }
      }
      setAvailableLots(lotsData);
    } catch (err) {
      console.error('Error loading lots:', err);
      setError('Failed to load available inventory');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle quantity changes.
   * For products:  handleQuantityChange(key, lotId, value, tier) — tier = 'pallets'|'cases'|'units'
   * For chemicals: handleQuantityChange(key, lotId, value) — tier unused
   */
  const handleQuantityChange = (key, lotId, value, tier = 'quantity', mode = 'container') => {
    if (isProduct) {
      const lot = availableLots[key]?.find(l => l.id === lotId);
      if (!lot) return;

      const numValue    = parseInt(value) || 0;
      const upc         = lot.units_per_case   || 1;
      const cpp         = lot.cases_per_pallet || 1;
      const totalBudget = lot.total_base_units  || 0;

      // Budget from OTHER two tiers
      const cur = selectedItems[key]?.[lotId] || {};
      const curPallets = tier === 'pallets' ? 0 : (cur.pallets || 0);
      const curCases   = tier === 'cases'   ? 0 : (cur.cases   || 0);
      const curUnits   = tier === 'units'   ? 0 : (cur.units   || 0);

      let maxVal;
      if (tier === 'pallets') {
        maxVal = Math.max(0, Math.min(
          lot.pallet_quantity || 0,
          Math.floor(Math.max(0, totalBudget - curCases * upc - curUnits) / (cpp * upc))
        ));
      } else if (tier === 'cases') {
        maxVal = Math.max(0, Math.min(
          (lot.case_quantity || 0) + (lot.pallet_quantity || 0) * cpp,
          Math.floor(Math.max(0, totalBudget - curPallets * cpp * upc - curUnits) / upc)
        ));
      } else {
        maxVal = Math.max(0, totalBudget - curPallets * cpp * upc - curCases * upc);
      }

      const finalQty = Math.max(0, Math.min(numValue, maxVal));

      setSelectedItems(prev => ({
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          [lotId]: {
            ...(prev[key]?.[lotId] || { pallets: 0, cases: 0, units: 0 }),
            [tier]:       finalQty,
            lot_number:   lot.lot_number,
            batch_number: lot.batch_number
          }
        }
      }));
    } else {
      // Chemicals — single quantity, supports container or measure mode
      const lot = availableLots[key]?.find(l => l.id === lotId);
      if (!lot) return;

      let finalQty, maxQty;
      if (mode === 'measure') {
        const numValue = parseFloat(value) || 0;
        maxQty   = lot.current_total_quantity || 0;
        finalQty = Math.max(0, Math.min(numValue, maxQty));
      } else {
        const numValue = parseInt(value) || 0;
        maxQty   = lot.current_quantity || 0;
        finalQty = Math.max(0, Math.min(numValue, maxQty));
      }

      setSelectedItems(prev => ({
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          [lotId]: {
            quantity:     finalQty,
            quantityMode: mode,
            lot_number:   lot.lot_number,
            batch_number: lot.batch_number,
            maxQuantity:  maxQty
          }
        }
      }));
    }
  };

  const getTotalTransferItems = () => {
    let count = 0;
    Object.values(selectedItems).forEach(lots => {
      Object.values(lots).forEach(lot => {
        if (isProduct) {
          if ((lot.pallets || 0) + (lot.cases || 0) + (lot.units || 0) > 0) count++;
        } else {
          if (lot.quantity > 0) count++;
        }
      });
    });
    return count;
  };

  const handleNext = () => setActiveStep(prev => prev + 1);
  const handleBack = () => setActiveStep(prev => prev - 1);

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const transfers = [];

      if (isProduct) {
        Object.keys(selectedItems).forEach(key => {
          const [itemId, variantPart] = key.split('_');
          const variantId = variantPart === 'null' ? null : variantPart;
          Object.keys(selectedItems[key]).forEach(lotId => {
            const lot = selectedItems[key][lotId];
            const totalTiers = (lot.pallets || 0) + (lot.cases || 0) + (lot.units || 0);
            if (totalTiers > 0) {
              transfers.push({
                itemId,
                variantId,
                lotId,
                pallets:      lot.pallets      || 0,
                cases:        lot.cases        || 0,
                units:        lot.units        || 0,
                lot_number:   lot.lot_number,
                batch_number: lot.batch_number
              });
            }
          });
        });
      } else {
        Object.keys(selectedItems).forEach(itemId => {
          Object.keys(selectedItems[itemId]).forEach(lotId => {
            const lot = selectedItems[itemId][lotId];
            if (lot.quantity > 0) {
              transfers.push({
                itemId,
                lotId,
                quantity:     lot.quantity,
                quantityMode: lot.quantityMode || 'container',
                lot_number:   lot.lot_number,
                batch_number: lot.batch_number
              });
            }
          });
        });
      }

      if (transfers.length === 0) {
        setError('No items selected for transfer');
        setLoading(false);
        return;
      }
      if (fromLocation.id === toLocation.id) {
        setError('From and To location cannot be the same');
        setLoading(false);
        return;
      }

      await config.executeTransfer(companyId, {
        fromLocationId: fromLocation.id,
        toLocationId:   toLocation.id,
        transfers,
        notes
      });

      onSuccess?.(`Successfully transferred ${transfers.length} item(s) from ${fromLocation.name} to ${toLocation.name}`);
      onClose();
    } catch (err) {
      console.error('Transfer error:', err);
      setError(err.message || 'Failed to complete transfer');
    } finally {
      setLoading(false);
    }
  };

  const sameLocation    = fromLocation && toLocation && fromLocation.id === toLocation.id;
  const canProceedStep0 = fromLocation !== null;
  const canProceedStep1 = fromLocation !== null && toLocation !== null && !sameLocation;
  const canProceedStep2 = getTotalTransferItems() > 0 && !sameLocation;

  if (!config) return null;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => { if (reason !== 'backdropClick') onClose(); }}
      disableEscapeKeyDown
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: 'none',
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
          backdropFilter: 'blur(20px)',
          boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
          overflow: 'hidden',
          maxHeight: '90vh'
        }
      }}
    >
      {/* Header */}
      <Box sx={{
        p: 3,
        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
        color: theme.palette.text.primary,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
          <Box sx={{
            p: 1.5, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <SwapHorizIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 500, mb: 0.5 }}>
              Transfer {config.displayName}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Move inventory between locations
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onBack && (
            <IconButton onClick={onBack} sx={{
              color: theme.palette.text.secondary,
              bgcolor: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04), transform: 'scale(1.05)' },
              transition: 'all 0.2s ease'
            }}>
              <ArrowBackIcon sx={{ fontSize: 24 }} />
            </IconButton>
          )}
          <IconButton onClick={onClose} sx={{
            color: theme.palette.text.secondary,
            bgcolor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04), transform: 'scale(1.05)' },
            transition: 'all 0.2s ease'
          }}>
            <CloseIcon sx={{ fontSize: 28 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: 0, maxHeight: 'calc(90vh - 120px)', overflowY: 'auto' }}>
        <Box sx={{ p: 3 }}>
          <Stepper activeStep={activeStep} orientation="vertical">

            {/* Step 0: Select From Location */}
            <Step>
              <StepLabel>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>{steps[0]}</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Select the source location for this transfer
                </Typography>
                {clearedOtherStep === 'to' && (
                  <Alert severity="info" onClose={() => setClearedOtherStep(null)} sx={{ mb: 2, borderRadius: 2 }}>
                    We cleared &quot;To location&quot; because you can&apos;t transfer to the same location.
                  </Alert>
                )}
                <Button fullWidth variant="outlined" onClick={() => setFromLocationDialogOpen(true)} sx={{
                  justifyContent: 'flex-start', textAlign: 'left', py: 2, px: 2, minHeight: 56,
                  borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.9)',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  color: fromLocation ? 'text.primary' : 'text.secondary',
                  fontWeight: 500, textTransform: 'none', fontSize: '0.9rem', mb: 3,
                  '&:hover': { border: `1px solid ${theme.palette.primary.main}`, backgroundColor: 'rgba(255,255,255,0.95)' }
                }}>
                  {fromLocation ? fromLocation.name : 'Select From Location'}
                </Button>
                <Button variant="contained" onClick={handleNext} disabled={!canProceedStep0}
                  sx={{ borderRadius: 2, px: 5, py: 1.8, fontWeight: 500, fontSize: '1rem' }}>
                  Continue
                </Button>
              </StepContent>
            </Step>

            {/* Step 1: Select To Location */}
            <Step>
              <StepLabel>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>{steps[1]}</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}>
                  Transferring from: <strong>{fromLocation?.name}</strong>
                </Typography>
                {clearedOtherStep === 'from' && (
                  <Alert severity="info" onClose={() => setClearedOtherStep(null)} sx={{ mb: 2, borderRadius: 2 }}>
                    We cleared &quot;From location&quot; because you can&apos;t transfer to the same location.
                  </Alert>
                )}
                <Button fullWidth variant="outlined" onClick={() => setToLocationDialogOpen(true)} sx={{
                  justifyContent: 'flex-start', textAlign: 'left', py: 2, px: 2, minHeight: 56,
                  borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.9)',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  color: toLocation ? 'text.primary' : 'text.secondary',
                  fontWeight: 500, textTransform: 'none', fontSize: '0.9rem', mb: 3,
                  '&:hover': { border: `1px solid ${theme.palette.primary.main}`, backgroundColor: 'rgba(255,255,255,0.95)' }
                }}>
                  {toLocation ? toLocation.name : 'Select To Location'}
                </Button>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button onClick={handleBack} sx={{ borderRadius: 2, px: 4, py: 1.8, fontSize: '1rem' }}>Back</Button>
                  <Button variant="contained" onClick={handleNext} disabled={!canProceedStep1}
                    sx={{ borderRadius: 2, px: 5, py: 1.8, fontWeight: 500, fontSize: '1rem' }}>
                    Continue
                  </Button>
                </Box>
              </StepContent>
            </Step>

            {/* Step 2: Configure Transfer Items */}
            <Step>
              <StepLabel>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>{steps[2]}</Typography>
              </StepLabel>
              <StepContent>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Typography variant="body2" sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}>
                      From: <strong>{fromLocation?.name}</strong> → To: <strong>{toLocation?.name}</strong>
                    </Typography>

                    {Object.keys(availableLots).length === 0 ? (
                      <Alert severity="warning">No inventory available at {fromLocation?.name}</Alert>
                    ) : (
                      <TransferItemsTable
                        availableLots={availableLots}
                        items={items}
                        selectedItems={selectedItems}
                        onQuantityChange={handleQuantityChange}
                        isChemical={inventoryType === 'chemicals'}
                        isProduct={isProduct}
                      />
                    )}

                    <TextField
                      fullWidth multiline rows={3} label="Notes (Optional)"
                      value={notes} onChange={(e) => setNotes(e.target.value)}
                      sx={{ mt: 3, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
                    />

                    <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                      <Button onClick={handleBack} sx={{ borderRadius: 2, px: 4, py: 1.8, fontSize: '1rem' }}>Back</Button>
                      <Button variant="contained" onClick={handleSubmit} disabled={loading || !canProceedStep2}
                        sx={{ borderRadius: 2, px: 5, py: 1.8, fontWeight: 500, fontSize: '1rem' }}>
                        {loading ? 'Processing...' : 'Submit Transfer'}
                      </Button>
                    </Box>
                  </>
                )}
              </StepContent>
            </Step>
          </Stepper>
        </Box>
      </Box>

      {/* Location dialogs */}
      <LocationSelectDialog
        open={fromLocationDialogOpen}
        onClose={() => { setFromLocationDialogOpen(false); setFromSearchTerm(''); }}
        title="Select From Location"
        locations={sortedLocations}
        searchTerm={fromSearchTerm}
        onSearchChange={setFromSearchTerm}
        onSelect={(loc) => {
          setFromLocation(loc);
          if (toLocation && loc.id === toLocation.id) { setToLocation(null); setClearedOtherStep('to'); }
          else setClearedOtherStep(null);
          setFromLocationDialogOpen(false); setFromSearchTerm('');
        }}
        excludeLocationId={fromLocation ? null : toLocation?.id}
      />
      <LocationSelectDialog
        open={toLocationDialogOpen}
        onClose={() => { setToLocationDialogOpen(false); setToSearchTerm(''); }}
        title="Select To Location"
        locations={sortedLocations}
        searchTerm={toSearchTerm}
        onSearchChange={setToSearchTerm}
        onSelect={(loc) => {
          setToLocation(loc);
          if (fromLocation && loc.id === fromLocation.id) { setFromLocation(null); setClearedOtherStep('from'); }
          else setClearedOtherStep(null);
          setToLocationDialogOpen(false); setToSearchTerm('');
        }}
        excludeLocationId={toLocation ? null : fromLocation?.id}
      />
    </Dialog>
  );
}
